/* eslint-disable @typescript-eslint/no-explicit-any */
import ArrowForwardIosOutlined from '@mui/icons-material/ArrowForwardIosOutlined'
import { Box, Button, CircularProgress, FormHelperText, Typography } from '@mui/material'
import { useCallback, useEffect, useMemo, useRef } from 'react'
import ReCAPTCHA from 'react-google-recaptcha'
import { useTranslation } from 'react-i18next'
import { IoIosArrowBack } from 'react-icons/io'
import { useLocation, useNavigate } from 'react-router-dom'

import DynamicForm from '../../components/DynamicForm'
import SocialLogin from '../../components/SocialLogin'
import SignOut from '../../components/SocialLogin/SignOut'
import { useFirebase } from '../../contexts/firebase'
import useClientConfigNormalizer from '../../hooks/useClientConfigNormalizer'
import useClientData from '../../hooks/useClientData'
import useConfiguration from '../../hooks/useConfiguration'
import useLogin from '../../hooks/useLogin'
import { useStore } from '../../state/client'
import { StackOverflow } from '../../styles/global'
import HomeSkeleton from '../Home/HomeSkeleton'
import ClientSkeleton from './ClientSkeleton'
import { HeaderText, ButtonText } from './styles'

const Client = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { t } = useTranslation()

  const { clientData, removeClientDataFromLocalStorage } = useClientData()
  const { configuration } = useConfiguration()
  const { clientConfigNormalizer } = useClientConfigNormalizer()
  const { user, isFirebaseLoading, signInWithGoogle, signInWithFacebook, logout } = useFirebase()
  const { socialToken } = useLogin()

  const recaptchaRef = useRef<ReCAPTCHA>(null)

  const initialData = useStore((state) => state.initialData)
  const currentStep = useStore((state) => state.currentStep)
  const isLoading = useStore((state) => state.isLoading)
  const redirectSuccessful = useStore((state) => state.redirectSuccessful)
  const redirectLogin = useStore((state) => state.redirectLogin)
  const formErrors = useStore((state) => state.formErrors)
  const previousStep = useStore((state) => state.previousStep)
  const nextStep = useStore((state) => state.nextStep)
  const loadForm = useStore((state) => state.loadForm)
  const submitForm = useStore((state) => state.submitForm)
  const submitAddressForm = useStore((state) => state.submitAddressForm)

  const handleBackButton = useCallback(() => {
    if (currentStep > 1) {
      previousStep()
      return
    }
    navigate(-1)
  }, [currentStep, previousStep, navigate])

  const handleRecaptchaExpired = useCallback(() => {
    recaptchaRef.current?.reset()
  }, [recaptchaRef])

  const configWebview = useMemo(() => {
    return clientConfigNormalizer(configuration?.extensions.clientWebview)
  }, [clientConfigNormalizer, configuration?.extensions.clientWebview])

  const numberSteps = useMemo(() => {
    return Object.keys(configWebview?.steps).length
  }, [configWebview])

  const stepConfig = useMemo(() => {
    return configWebview?.steps[currentStep]
  }, [configWebview, currentStep])

  const lastStep = useMemo(() => {
    return currentStep === numberSteps
  }, [currentStep, numberSteps])

  useEffect(() => {
    if (formErrors) {
      recaptchaRef.current?.reset()
    }
  }, [formErrors])

  useEffect(() => {
    loadForm(clientData, configWebview.initialData)
  }, [clientData, configWebview.initialData, loadForm])

  useEffect(() => {
    if (redirectSuccessful) {
      removeClientDataFromLocalStorage() // ESTÁ REMOVENDO MAS APÓS REDIRECT VOLTA PRO LOCALSTORAGE
      navigate(`/${configuration?.slug}/redirect${location.search}`, { state: location.pathname })
    }
  }, [configuration, location, navigate, redirectSuccessful, removeClientDataFromLocalStorage])

  useEffect(() => {
    if (redirectLogin) {
      navigate(`/${configuration?.slug}/signIn${location.search}`, { state: location.pathname })
    }
  }, [configuration, location, navigate, redirectLogin])

  const submit = useCallback(
    async (data: any) => {
      if (stepConfig.type === 'address') {
        return submitAddressForm(data, stepConfig.submitData, configuration)
      }
      const recaptchaCode = await recaptchaRef.current?.executeAsync()
      return submitForm({ ...data, socialToken, recaptchaCode }, stepConfig.submitData, configuration)
    },
    [configuration, socialToken, stepConfig, submitAddressForm, submitForm, recaptchaRef]
  )

  const handleSubmit = useCallback(
    async (data: any) => {
      let success = true
      if (stepConfig.submitData) {
        success = await submit(data)
      }
      if (success && !lastStep) {
        nextStep()
      }
    },
    [lastStep, nextStep, stepConfig, submit]
  )

  if (!configuration || isFirebaseLoading) {
    return <HomeSkeleton />
  }

  return (
    <StackOverflow direction="column" p="1.5rem 0" height="100%">
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        padding="0 1.5rem 1.125rem 0"
        borderBottom="0.5px solid #C9CCCF"
      >
        <Button
          onClick={handleBackButton}
          style={{
            ...configuration.extensions.webviewCustomizations?.unstyledButton,
            padding: 0,
            minWidth: 0,
            marginLeft: '1rem',
          }}
        >
          <IoIosArrowBack size={30} color="#4E5256" />
        </Button>
        <HeaderText>{t('client.signUp')}</HeaderText>
        <Box />
      </Box>

      <FormHelperText style={{ textAlign: 'center', fontSize: '0.938rem', padding: '0.75rem 1.5rem' }}>
        Etapa {currentStep} de {numberSteps}
      </FormHelperText>

      <Typography variant="h5" margin="0 1.5rem">
        {stepConfig.pageTitle}
        <FormHelperText>Os campos marcados com (*) são obrigatórios</FormHelperText>
      </Typography>

      {currentStep === 1 &&
        (user ? (
          <SignOut
            email={user?.email}
            onSignOutClick={logout}
            displayName={user?.displayName}
            photoURL={user?.photoURL}
            customerColor={configuration.webview.theme.customerColor}
            provider={user?.providerData[0].providerId.replace('.com', '')}
            padding="1.688rem 1.5rem"
          />
        ) : (
          <SocialLogin
            googleSignIn={signInWithGoogle}
            facebookSignIn={signInWithFacebook}
            signInText={t('client.connectSocialNetwork')}
            padding="1.688rem 1.5rem"
          />
        ))}

      {!initialData ? (
        <ClientSkeleton />
      ) : (
        <DynamicForm
          flex={1}
          padding="0 1.5rem"
          defaultValues={initialData}
          onSubmit={handleSubmit}
          formElements={stepConfig.elements}
          formErrors={formErrors}
        >
          <Box marginTop={2}>
            <Button
              fullWidth
              size="large"
              type="submit"
              variant="contained"
              disabled={isLoading}
              endIcon={
                isLoading ? (
                  <CircularProgress size="1rem" style={{ color: '#fff' }} />
                ) : (
                  <ArrowForwardIosOutlined style={{ fontSize: '1rem' }} />
                )
              }
              style={{ ...configuration.extensions.webviewCustomizations?.primaryButton, padding: '1rem' }}
            >
              <ButtonText variant="button">{lastStep ? 'Finalizar Cadastro' : 'Continuar'}</ButtonText>
            </Button>
          </Box>
        </DynamicForm>
      )}

      <ReCAPTCHA
        size="invisible"
        ref={recaptchaRef}
        onExpired={handleRecaptchaExpired}
        sitekey={configuration.extensions.recaptchaSiteKey}
        badge="bottomleft"
      />
    </StackOverflow>
  )
}

export default Client
